import { motion } from 'framer-motion';
import { Mic, Timer, ImageIcon, Edit3, TrendingUp } from 'lucide-react';

const features = [
  {
    icon: Mic,
    title: 'Lifelike AI Voiceovers',
    description: 'Pick from 40+ studio-quality voices. Sound professional without buying a microphone, booking a studio, or hiring anyone.',
    tags: ['40+ voices', 'Multiple languages', 'All ages'],
    size: 'large',
  },
  {
    icon: Timer,
    title: 'Auto-Captions & Sync',
    description: '85% of social videos are watched on mute. AdoriAI auto-generates perfectly timed captions so your message lands regardless.',
    tags: ['Auto-synced', 'Editable', 'Pre-built templates'],
    size: 'small',
  },
  {
    icon: ImageIcon,
    title: '100M+ Media Assets',
    description: 'The AI picks the most relevant photos, clips, and music from a massive copyright-free library. No more spending hours hunting for b-roll.',
    tags: ['Copyright-free', 'AI-curated', 'Always fresh'],
    size: 'small',
  },
  {
    icon: Edit3,
    title: 'Live Video Editor',
    description: 'Don\'t like a scene? Swap it in real time. Preview changes side-by-side with zero export delays. Your video, your way — fast.',
    tags: ['Scene-based', 'Live preview', 'Animations'],
    size: 'large',
  },
];

const seoFeature = {
  icon: TrendingUp,
  title: 'Built-in YouTube SEO',
  description: 'Your videos are optimized to rank before you even hit publish. AdoriAI suggests titles, tags, and descriptions based on real search trends — so new readers discover you through video, not just Google.',
  tags: ['Title suggestions', 'Tag optimization', 'Description writer', 'Trend matching'],
  seoScore: {
    title: 92,
    tags: 88,
    visibility: 95,
  },
};

const FeaturesSection = () => {
  return (
    <section id="features" className="py-24 px-6 bg-surface-elevated/30" data-testid="features-section">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-accent mb-4 block">
            Features
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            Everything a solo creator needs.
            <br />
            <span className="text-gray-400">Nothing you'll never use.</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            One platform. No subscriptions to five separate tools.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`${
                feature.size === 'large' ? 'md:col-span-8' : 'md:col-span-4'
              } bg-surface rounded-2xl p-8 border border-white/10 hover:border-brand-primary/30 transition-all duration-300 group`}
            >
              <div className="w-12 h-12 rounded-xl bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center mb-6 group-hover:bg-brand-primary/20 transition-colors">
                <feature.icon className="w-6 h-6 text-brand-primary" />
              </div>
              
              <h3 className="text-xl font-heading font-bold text-white mb-3">{feature.title}</h3>
              <p className="text-gray-400 leading-relaxed mb-4">{feature.description}</p>
              
              <div className="flex flex-wrap gap-2">
                {feature.tags.map((tag, tagIndex) => (
                  <span 
                    key={tagIndex}
                    className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10 text-gray-400"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}

          {/* SEO Feature - Full Width */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="md:col-span-12 bg-surface rounded-2xl p-8 border border-brand-primary/20 hover:border-brand-primary/40 transition-all duration-300 group relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/5 to-transparent pointer-events-none"></div>
            
            <div className="relative grid md:grid-cols-2 gap-8">
              <div>
                <div className="w-12 h-12 rounded-xl bg-brand-primary/20 border border-brand-primary/30 flex items-center justify-center mb-6">
                  <seoFeature.icon className="w-6 h-6 text-brand-primary" />
                </div>
                
                <h3 className="text-2xl font-heading font-bold text-white mb-3">{seoFeature.title}</h3>
                <p className="text-gray-400 leading-relaxed mb-4">{seoFeature.description}</p>
                
                <div className="flex flex-wrap gap-2">
                  {seoFeature.tags.map((tag, tagIndex) => (
                    <span 
                      key={tagIndex}
                      className="text-xs px-3 py-1 rounded-full bg-brand-primary/10 border border-brand-primary/20 text-brand-accent"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* SEO Score Mockup */}
              <div className="bg-surface-elevated rounded-xl p-6 border border-white/10">
                <div className="text-sm text-gray-500 mb-4">SEO Score Preview</div>
                <div className="space-y-4">
                  {Object.entries(seoFeature.seoScore).map(([key, value]) => (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-400 capitalize">{key.replace('_', ' ')} relevance</span>
                        <span className="text-white font-semibold">{value}%</span>
                      </div>
                      <div className="h-2 bg-surface rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          whileInView={{ width: `${value}%` }}
                          transition={{ duration: 1, delay: 0.5 }}
                          viewport={{ once: true }}
                          className="h-full bg-gradient-to-r from-brand-primary to-brand-accent rounded-full"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
